import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import {
  getApiErrorMessage,
  getMixDashboard,
} from '../../../../api/mix-deliveriesApi';

export default function MixDashboardCards({ refreshKey = 0 }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getMixDashboard();
      setDashboard(result);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, refreshKey]);

  if (loading && !dashboard) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error && !dashboard) {
    return <Alert severity="error">{error}</Alert>;
  }

  const cards = [
    {
      label: 'Miksy do przygotowania',
      value: dashboard?.mixesToPrepare ?? 0,
      severity: 'primary',
    },
    {
      label: 'Do przygotowania',
      value: dashboard?.toPrepare ?? 0,
      severity: 'default',
    },
    {
      label: 'W trakcie',
      value: dashboard?.inProgress ?? 0,
      severity: 'info',
    },
    {
      label: 'Przygotowane dzisiaj',
      value: dashboard?.preparedToday ?? 0,
      severity: 'success',
    },
    {
      label: 'Uzupełnienie 0D20',
      value: dashboard?.replenishmentRequired ?? 0,
      severity: 'warning',
    },
    {
      label: 'Zmiana składu',
      value: dashboard?.storageChangesRequired ?? 0,
      severity: 'warning',
    },
    {
      label: 'Brak zapasu',
      value: dashboard?.stockShortages ?? 0,
      severity: 'error',
    },
  ];

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nie udało się odświeżyć liczników: {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(7, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <DashboardCard
            key={card.label}
            label={card.label}
            value={card.value}
            severity={card.severity}
          />
        ))}
      </Box>
    </Box>
  );
}

function DashboardCard({ label, value, severity }) {
  const colorMap = {
    default: 'text.primary',
    primary: 'primary.main',
    info: 'info.main',
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        height: '100%',
      }}
    >
      <Typography
        variant="h4"
        fontWeight={700}
        color={colorMap[severity] ?? 'text.primary'}
      >
        {value}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}
